// FieldIQ — construction "Equipment" tab (asset-recurrence Phase 5b). The crew's
// read-only view of the facility's assets and their maintenance plans: what
// equipment / yards / units exist and what service each is on. Reads the
// crew-redacted BuildIQ asset registry (biqGetAssets) — the maintenance plan
// (trade / vendor / cadence) comes through, but the lease block never does
// (office-only, stripped server-side). Crew can't edit assets; the actual
// maintenance work shows up as jobs in the Work Log when the engine schedules it.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Home, HardHat, Loader2, MapPin, Navigation, Package, RefreshCw, Search, Wrench, WifiOff,
} from "lucide-react";
import { biqGetAssets } from "../lib/buildiq";

const KIND_ICON = { equipment: Package, yard: MapPin, unit: Home };
const KIND_LABEL = { equipment: "Equipment", yard: "Yard", unit: "Unit" };

const cadence = (rule) => {
  const n = Number(rule && rule.every && rule.every.n) || 0;
  const u = (rule && rule.every && rule.every.unit) || "";
  if (!n || !u) return "";
  return u === "hours" ? `every ${n} hrs` : `every ${n} ${u}`;
};

const addressOf = (a) => (a && a.identifiers && a.identifiers.address) || "";
const mapsUrl = (a) => `https://maps.google.com/?q=${encodeURIComponent(addressOf(a))}`;

export default function AssetsTab({ isOnline }) {
  const [db, setDb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setErr("");
    biqGetAssets()
      .then((d) => (d && d.error ? setErr(d.error) : setDb(d)))
      .catch(() => setErr("network"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const assets = useMemo(() => {
    const map = (db && db.assets) || {};
    let list = Object.values(map).filter((a) => a && a.id);
    const s = q.trim().toLowerCase();
    if (s) {
      list = list.filter((a) =>
        `${a.label || ""} ${a.kind || ""} ${addressOf(a)} ${(a.identifiers && a.identifiers.serial) || ""}`
          .toLowerCase().includes(s));
    }
    return list.sort((a, b) => String(a.label || a.id).localeCompare(String(b.label || b.id)));
  }, [db, q]);

  if (loading)
    return (
      <div className="flex items-center justify-center h-48 text-slate-400">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading equipment…
      </div>
    );

  if (err)
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-slate-500">
        {err === "network" ? <WifiOff size={28} className="text-slate-700" /> : <Package size={28} className="text-slate-700" />}
        <p className="text-sm font-medium">Couldn’t load equipment.</p>
        <button type="button" onClick={load} className="flex items-center gap-2 text-sky-400 text-sm">
          <RefreshCw size={14} /> Try again
        </button>
      </div>
    );

  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-slate-400">
        <Search size={16} />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Find equipment, a yard, or a unit"
          aria-label="Search equipment"
          className="flex-1 bg-transparent outline-none text-slate-200 text-sm placeholder:text-slate-500"
        />
      </label>

      {!assets.length ? (
        <div className="flex flex-col items-center justify-center py-12 text-center gap-3">
          <HardHat size={32} className="text-slate-700" />
          <p className="text-slate-400 text-sm font-medium">{q ? "No matches." : "No equipment yet"}</p>
          {!q && <p className="text-slate-500 text-xs">Equipment, yards, and units with a maintenance plan show up here.</p>}
        </div>
      ) : (
        <div className="space-y-2">
          {assets.map((a) => {
            const Icon = KIND_ICON[a.kind] || Package;
            const plan = Array.isArray(a.maintenance) ? a.maintenance.filter((r) => r && r.active !== false) : [];
            const hours = a.meta && a.meta.hours;
            const addr = addressOf(a);
            return (
              <div key={a.id} className="bg-slate-800 border border-slate-700 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <span className="shrink-0 w-9 h-9 rounded-lg bg-slate-700/60 text-sky-300 flex items-center justify-center">
                    <Icon size={17} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-slate-100 font-semibold truncate">{a.label || "Untitled"}</div>
                    <div className="text-slate-400 text-xs mt-0.5">
                      {KIND_LABEL[a.kind] || a.kind}
                      {a.kind === "equipment" && (hours || hours === 0) ? ` · ${Number(hours).toLocaleString()} hrs` : ""}
                      {a.identifiers && a.identifiers.serial ? ` · #${a.identifiers.serial}` : ""}
                    </div>
                  </div>
                </div>

                {addr && (
                  <div className="flex items-start gap-1.5 mt-2 text-slate-300 text-sm">
                    <MapPin size={15} className="text-slate-500 mt-0.5 shrink-0" />
                    <span className="min-w-0">{addr}</span>
                  </div>
                )}

                {plan.length > 0 && (
                  <div className="mt-3 space-y-1.5">
                    {plan.map((r, i) => (
                      <div key={r.id || i} className="flex items-center gap-2 text-xs">
                        <Wrench size={13} className="text-slate-500 shrink-0" />
                        <span className="text-slate-200 font-medium truncate">{r.label || "Service"}</span>
                        {cadence(r) && <span className="text-slate-500">· {cadence(r)}</span>}
                        {r.vendor && <span className="text-slate-500 truncate">· {r.vendor}</span>}
                      </div>
                    ))}
                  </div>
                )}

                {addr && (
                  <a
                    href={mapsUrl(a)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sky-400 text-sm font-medium mt-3"
                  >
                    <Navigation size={14} /> Directions
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!isOnline && (
        <p className="text-slate-500 text-xs text-center pt-1">Offline — showing the last synced equipment.</p>
      )}
    </div>
  );
}
